import { db } from "@/main";
import { DevicesModule } from "@/store/modules/DevicesModule";
import { firestore } from "firebase";
import { DeviceOverview } from "../models/DeviceOverview";
import { getClientId, getClientRef } from "./ClientRepository";
import { iresAxiosInstance } from "@/api/IresApi";

interface DeviceOverviewPage {
  pageNumber: number;
  devices: DeviceOverview[];
}

function mapSnapshotToDeviceOverviews(
  snapshot: firestore.QuerySnapshot<firestore.DocumentData>,
  filter?: (DeviceOverview: DeviceOverview) => boolean
) {
  const pages: DeviceOverviewPage[] = [];
  const result = new Map<string, DeviceOverview>();

  snapshot.forEach((doc) => {
    const tokens = doc.id.split("_");

    const page: DeviceOverviewPage = {
      pageNumber: parseInt(tokens[tokens.length - 1]),
      devices: [],
    };

    const data = doc.get("data");

    if (data) {
      Object.keys(data).forEach((key) => {
        const overview = data[key] as DeviceOverview;
  
        if (overview && overview.id && (!filter || filter(overview))) {
          page.devices.push(overview);
        }
      });
  
      pages.push(page);
    }
  });

  pages
    .sort((p1, p2) => p2.pageNumber - p1.pageNumber)
    .forEach((page) => {
      page.devices.forEach((device) => {
        if (!result.has(device.id)) {
          result.set(device.id, device);
        }
      });
    });

  return [...result.values()];
}

export function observeDeviceOverviews(
  onNext: (deviceOverviews: Array<DeviceOverview>) => void
) {
  const cachedDeviceOverviews = [...DevicesModule.deviceOverviewCache.values()];
  onNext(cachedDeviceOverviews);

  const refPath = getClientRef() + "/deviceOverviews";
  const ref = db.collection(refPath);

  return ref.onSnapshot((snapshot) => {
    const deviceOverviews = mapSnapshotToDeviceOverviews(snapshot).filter(
      (deviceOverview) => !deviceOverview.deleted
    );

    deviceOverviews.forEach((deviceOverview) =>
      DevicesModule.cacheDeviceOverview(deviceOverview)
    );

    onNext(deviceOverviews);
  });
}

export async function fetchDeviceOverviews(): Promise<Array<DeviceOverview>> {
  const refPath = getClientRef() + "/deviceOverviews";
  const ref = db.collection(refPath);

  const snapshot = await ref.get();
  const deviceOverviews = mapSnapshotToDeviceOverviews(snapshot).filter(
    (deviceOverview) => !deviceOverview.deleted
  );

  deviceOverviews.forEach((deviceOverview) =>
    DevicesModule.cacheDeviceOverview(deviceOverview)
  );

  return deviceOverviews;
}

/**
 * Copies all devices to the client's administration section.
 * @deprecated This function is currently not in use.
 * @returns {Promise<AxiosResponse>} The response object from the HTTP request.
 */
export async function copyAllDevicesToClientsAdministration() {
  const payload = {
    administratorClientId: getClientId(),
  };

  const response = await iresAxiosInstance.put(
    `https://europe-west1-ires-cloud.cloudfunctions.net/webApi/ires/v1/copyAllDevicesToClientsAdministration`,
    payload
  );

  return response;
}